import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { Country, Language, User } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { FADE_IN_CONTENT_BY_HEIGHT_OPACITY } from '@app/core/constants/animations.constant';
import { DpGtmService } from '@app/core/services/gtm.service';
import { DpMeasurementSystemService } from '@app-modules/site-context/facade/measurement-system.service';
import { MeasurementSystemsActions } from '@app-modules/site-context/store/actions/measurement-systems.action';
import { MeasurementSystem } from '@app-modules/site-context/types/measurement-system.type';

import { DpSiteContextService } from '../../services';
import { toggleEditBarVisibility } from '../../store/actions';
import { editBarVisibilitySelector } from '../../store/site-context.selectors';

@Component({
    selector: 'dp-site-context',
    templateUrl: './site-context.component.html',
    styleUrl: './site-context.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [FADE_IN_CONTENT_BY_HEIGHT_OPACITY],
    standalone: false
})
export class DpSiteContextComponent {
  userData$: Observable<string>;
  countries$: Observable<Country[]>;
  languages$: Observable<Language[]>;
  measurementSystems$: Observable<MeasurementSystem[]>;
  activeCountry$: Observable<Country | undefined>;
  activeMeasurementSystem$: Observable<string | undefined>;
  activeLanguageLabel$: Observable<string>;
  activeMeasurementSystemLabel$: Observable<string>;
  activeLanguage$: Observable<string>;
  editBarVisibility$: Observable<boolean>;

  readonly #store = inject(Store);
  readonly #siteContextService = inject(DpSiteContextService);
  readonly #userAccount = inject(UserAccountFacade);
  readonly #dpGtmService = inject(DpGtmService);
  readonly #measurementSystemService = inject(DpMeasurementSystemService);

  constructor() {
    this.userData$ = this.#userData$;
    this.activeCountry$ = this.#siteContextService.getActiveCountry$();
    this.activeLanguageLabel$ = this.#siteContextService.getActiveLanguageLabel$();
    this.activeLanguage$ = this.#siteContextService.getActiveLanguage$();
    this.activeMeasurementSystem$ = this.#measurementSystemService.getActive();
    this.editBarVisibility$ = this.#store.select(editBarVisibilitySelector);
    this.countries$ = this.#siteContextService.getCountries$();
    this.languages$ = this.#siteContextService.languages$;
    this.measurementSystems$ = this.#measurementSystemService.getAll();
    this.activeMeasurementSystemLabel$ = this.#measurementSystemService.getActiveLabel();
  }

  get #userData$(): Observable<string> {
    return (this.#userAccount.get() as Observable<User>).pipe(
      map((user?: User) => (user?.name ? user?.name + (user?.erpId ? ` - ${user.erpId}` : '') : ''))
    );
  }

  toggleEditBarVisibility(): void {
    this.#store.dispatch(toggleEditBarVisibility());
  }

  changeLanguage(language: Language): void {
    this.#siteContextService.setActiveLanguage(language);
    this.#userAccount
      .get()
      .pipe(take(1))
      .subscribe((user) => {
        void this.#dpGtmService.initGtmPush(user as User);
      });
  }

  changeMeasurementSystem(value: MeasurementSystem): void {
    this.#store.dispatch(MeasurementSystemsActions.changeActiveMeasurementSystem({ payload: value.code }));
  }

  changeCountry(country: Country): void {
    this.#siteContextService.setActiveCountry(country);
  }
}
